import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import RoleBasedHeader from '../components/ui/RoleBasedHeader';
import Icon from '../components/AppIcon';

const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#1e4a72] focus:ring-offset-2 ${checked ? 'bg-[#1e4a72]' : 'bg-gray-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`}
    />
  </button>
);

const ToggleRow = ({ label, description, field, value, onChange, saving }) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {saving === field && (
        <span className="text-xs text-green-600 font-medium animate-pulse">Saved</span>
      )}
      <ToggleSwitch
        checked={!!value}
        onChange={(val) => onChange(field, val)}
        disabled={saving === field}
      />
    </div>
  </div>
);

const SectionCard = ({ icon, iconBg, iconColor, title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-5">
    <div className="flex items-center gap-2 mb-4">
      <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
        <Icon name={icon} size={16} className={iconColor} />
      </div>
      <h2 className="text-base font-semibold text-gray-800">{title}</h2>
    </div>
    <div className="divide-y divide-gray-50">{children}</div>
  </div>
);

const NotificationSettings = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [prefs, setPrefs] = useState({
    whatsapp_enabled: false,
    email_enabled: false,
    sms_enabled: false,
    notify_booking_confirmed: false,
    notify_quota_available: false,
    notify_truck_assigned: false,
    notify_loading_started: false,
    notify_delivery_completed: false,
    notify_payment_received: false,
    notify_weekly_summary: false,
    notify_factory_quota_listed: false,
    notify_approval_requests: false,
  });
  const [userRole, setUserRole] = useState('');
  const [isFetching, setIsFetching] = useState(true);
  const [savingField, setSavingField] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate('/login-authentication');
  }, [user, loading, navigate]);

  useEffect(() => {
    const role = localStorage.getItem('userRole') || '';
    setUserRole(role?.toLowerCase()?.trim());
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchPrefs = async () => {
      setIsFetching(true);
      try {
        const { data, error } = await supabase?.from('notification_preferences')?.select('*')?.eq('user_id', user?.id)?.single();
        if (!error && data) {
          setPrefs({
            whatsapp_enabled: !!data?.whatsapp_enabled,
            email_enabled: !!data?.email_enabled,
            sms_enabled: !!data?.sms_enabled,
            notify_booking_confirmed: !!data?.notify_booking_confirmed,
            notify_quota_available: !!data?.notify_quota_available,
            notify_truck_assigned: !!data?.notify_truck_assigned,
            notify_loading_started: !!data?.notify_loading_started,
            notify_delivery_completed: !!data?.notify_delivery_completed,
            notify_payment_received: !!data?.notify_payment_received,
            notify_weekly_summary: !!data?.notify_weekly_summary,
            notify_factory_quota_listed: !!data?.notify_factory_quota_listed,
            notify_approval_requests: !!data?.notify_approval_requests,
          });
        }
      } catch (err) {
        console.error('Error fetching notification preferences:', err);
      } finally {
        setIsFetching(false);
      }
    };
    fetchPrefs();
  }, [user]);

  const handleToggle = useCallback(async (field, value) => {
    setPrefs(prev => ({ ...prev, [field]: value }));
    setSavingField(field);
    try {
      await supabase?.from('notification_preferences')?.update({ [field]: value })?.eq('user_id', user?.id);
    } catch (err) {
      // Revert on error
      setPrefs(prev => ({ ...prev, [field]: !value }));
      console.error('Error saving preference:', err);
    } finally {
      setTimeout(() => setSavingField(null), 1200);
    }
  }, [user]);

  const isFactoryAdmin = userRole === 'factory_admin' || userRole === 'factory' || userRole === 'admin';
  const isAdmin = userRole === 'admin';

  if (loading || isFetching) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-[#1e4a72] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Loading preferences...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <RoleBasedHeader />
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#1e4a72]">Notification Settings</h1>
            <p className="text-sm text-gray-500 mt-0.5">Changes are saved automatically</p>
          </div>
        </div>

        {/* Channels */}
        <SectionCard icon="Radio" iconBg="bg-blue-100" iconColor="text-[#1e4a72]" title="Notification Channels">
          <ToggleRow
            label="WhatsApp Notifications"
            description="Receive updates via WhatsApp messages"
            field="whatsapp_enabled"
            value={prefs?.whatsapp_enabled}
            onChange={handleToggle}
            saving={savingField}
          />
          <ToggleRow
            label="Email Notifications"
            description="Receive updates via email"
            field="email_enabled"
            value={prefs?.email_enabled}
            onChange={handleToggle}
            saving={savingField}
          />
          <ToggleRow
            label="SMS Notifications"
            description="Receive updates via SMS"
            field="sms_enabled"
            value={prefs?.sms_enabled}
            onChange={handleToggle}
            saving={savingField}
          />
        </SectionCard>

        {/* Booking Events */}
        <SectionCard icon="ClipboardList" iconBg="bg-orange-100" iconColor="text-[#ff8c00]" title="Booking Events">
          <ToggleRow
            label="Booking Confirmed"
            description="When your booking is approved and confirmed"
            field="notify_booking_confirmed"
            value={prefs?.notify_booking_confirmed}
            onChange={handleToggle}
            saving={savingField}
          />
          <ToggleRow
            label="Truck Assigned"
            description="When a truck is assigned to your order"
            field="notify_truck_assigned"
            value={prefs?.notify_truck_assigned}
            onChange={handleToggle}
            saving={savingField}
          />
          <ToggleRow
            label="Loading Started"
            description="When loading of your order begins"
            field="notify_loading_started"
            value={prefs?.notify_loading_started}
            onChange={handleToggle}
            saving={savingField}
          />
          <ToggleRow
            label="Delivery Completed"
            description="When your order is delivered successfully"
            field="notify_delivery_completed"
            value={prefs?.notify_delivery_completed}
            onChange={handleToggle}
            saving={savingField}
          />
        </SectionCard>

        {/* Marketplace */}
        <SectionCard icon="Store" iconBg="bg-green-100" iconColor="text-green-600" title="Marketplace">
          <ToggleRow
            label="New Quota Available"
            description="When new sugar quota is listed in the marketplace"
            field="notify_quota_available"
            value={prefs?.notify_quota_available}
            onChange={handleToggle}
            saving={savingField}
          />
          <ToggleRow
            label="Payment Received"
            description="When a payment is received for your order"
            field="notify_payment_received"
            value={prefs?.notify_payment_received}
            onChange={handleToggle}
            saving={savingField}
          />
          <ToggleRow
            label="Weekly Summary"
            description="Receive a weekly digest of your activity"
            field="notify_weekly_summary"
            value={prefs?.notify_weekly_summary}
            onChange={handleToggle}
            saving={savingField}
          />
        </SectionCard>

        {/* Admin (conditional) */}
        {(isFactoryAdmin || isAdmin) && (
          <SectionCard icon="ShieldCheck" iconBg="bg-purple-100" iconColor="text-purple-600" title="Admin Alerts">
            {isFactoryAdmin && (
              <ToggleRow
                label="Factory Quota Listed"
                description="When a new factory quota is listed on the platform"
                field="notify_factory_quota_listed"
                value={prefs?.notify_factory_quota_listed}
                onChange={handleToggle}
                saving={savingField}
              />
            )}
            {isAdmin && (
              <ToggleRow
                label="Approval Requests"
                description="When new user or order approvals are pending"
                field="notify_approval_requests"
                value={prefs?.notify_approval_requests}
                onChange={handleToggle}
                saving={savingField}
              />
            )}
          </SectionCard>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
